import { Fragment, useCallback, useEffect, useRef, useState, type MouseEvent } from "react";
import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { useDialogDismissGuard } from "@/components/ui/useDialogDismissGuard";
import { Spinner } from "@/components/ui/Spinner";
import { useMaMediaContextMenu } from "@/pages/material/useMaMediaContextMenu";
import { useModalStackLayer } from "@/lib/modalStack";
import type { CopyMediaSource } from "@/lib/clipboardMedia";

export interface LightboxSlide {
  url: string;
  filename?: string;
  contentType?: string | null;
  /** Wird sofort gezeigt und bleibt, wenn `url` nicht geladen werden kann. */
  fallbackUrl?: string;
}

export interface LightboxProps {
  /** Bild-URL oder null/undefined, wenn geschlossen. */
  url: string | null;
  onClose: () => void;
  /** Überliegende Modals (z. B. Projektübersicht z-[200]). */
  zIndexClass?: string;
  /** Quelle für Rechtsklick → Kopieren / Öffnen / Drucken. */
  copySource?: CopyMediaSource | null;
  /** Weitere Bilder. Pfeile und Wischen nur bei mehr als einem. */
  slides?: LightboxSlide[] | null;
  slideIndex?: number;
  onSlideIndexChange?: (index: number) => void;
}

function filenameFromUrl(url: string): string {
  try {
    const path = new URL(url, window.location.origin).pathname;
    const base = path.split("/").pop() || "";
    if (base && /\.(jpe?g|png|gif|webp|bmp|heic)$/i.test(base)) {
      return decodeURIComponent(base);
    }
  } catch {
    // ignore
  }
  return "bild.jpg";
}

const FRAME_CLASS =
  "relative flex h-[min(82dvh,calc(100dvh-env(safe-area-inset-top)-env(safe-area-inset-bottom)-5.5rem))] w-[min(96vw,1200px)] items-center justify-center";

const IMAGE_CLASS = "h-full w-full object-contain select-none";

export function Lightbox({
  url,
  onClose,
  zIndexClass = "z-[60]",
  copySource,
  slides = null,
  slideIndex = 0,
  onSlideIndexChange,
}: LightboxProps) {
  const count = slides?.length ?? 0;
  const safeIndex = count > 0 ? Math.min(Math.max(slideIndex, 0), count - 1) : 0;
  const slide = count > 0 ? slides![safeIndex] : null;
  const imageUrl = slide?.url ?? url;
  const fallbackUrl = slide?.fallbackUrl;
  const open = !!imageUrl;
  const filename =
    slide?.filename ||
    copySource?.filename ||
    (imageUrl ? filenameFromUrl(imageUrl) : "bild.jpg");
  const contentType = slide?.contentType || copySource?.contentType || "image/jpeg";

  const { openMediaContextMenu, contextMenuNode } = useMaMediaContextMenu();
  const guardedClose = useDialogDismissGuard(open, onClose);
  const { zIndex: stackZIndex } = useModalStackLayer(open);
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const [displayLoaded, setDisplayLoaded] = useState(false);
  const [displayFailed, setDisplayFailed] = useState(false);

  useEffect(() => {
    setDisplayLoaded(false);
    setDisplayFailed(false);
  }, [imageUrl]);

  const canPrev = count > 1 && safeIndex > 0;
  const canNext = count > 1 && safeIndex < count - 1;

  const goPrev = useCallback(() => {
    if (!canPrev) return;
    onSlideIndexChange?.(safeIndex - 1);
  }, [canPrev, onSlideIndexChange, safeIndex]);

  const goNext = useCallback(() => {
    if (!canNext) return;
    onSlideIndexChange?.(safeIndex + 1);
  }, [canNext, onSlideIndexChange, safeIndex]);

  useEffect(() => {
    if (!open) return;
    function handle(e: KeyboardEvent) {
      if (document.querySelector(".eh-context-menu")) return;
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        goNext();
      }
    }
    window.addEventListener("keydown", handle);
    return () => window.removeEventListener("keydown", handle);
  }, [open, onClose, goPrev, goNext]);

  const showFallback = Boolean(fallbackUrl) && fallbackUrl !== imageUrl && (displayFailed || !displayLoaded);
  const showDisplay = Boolean(imageUrl) && !displayFailed;

  function openMenu(event: MouseEvent, imageElement: HTMLImageElement | null, shownUrl: string) {
    openMediaContextMenu(event, {
      ...copySource,
      filename,
      contentType,
      url: shownUrl,
      isImage: true,
      imageElement,
    });
  }

  return (
    <>
      <Transition show={open} as={Fragment}>
        <Dialog
          as="div"
          className={clsx("relative touch-pan-y", zIndexClass)}
          style={stackZIndex != null ? { zIndex: stackZIndex } : undefined}
          onClose={guardedClose}
        >
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-150"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/85" aria-hidden />
          </TransitionChild>

          <div className="fixed inset-0 flex items-center justify-center p-3 sm:p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-150"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-100"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel
                className="relative flex max-w-[100vw] flex-col items-center"
                onTouchStart={(event) => {
                  const touch = event.changedTouches[0];
                  if (!touch) return;
                  touchStart.current = { x: touch.clientX, y: touch.clientY };
                }}
                onTouchEnd={(event) => {
                  const start = touchStart.current;
                  touchStart.current = null;
                  if (!start) return;
                  const touch = event.changedTouches[0];
                  if (!touch) return;
                  const dx = touch.clientX - start.x;
                  const dy = touch.clientY - start.y;
                  if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy)) return;
                  if (dx < 0) goNext();
                  else goPrev();
                }}
              >
                <div className={FRAME_CLASS}>
                  {showFallback && fallbackUrl ? (
                    <img
                      src={fallbackUrl}
                      alt={filename}
                      crossOrigin="use-credentials"
                      draggable={false}
                      className={IMAGE_CLASS}
                      onContextMenu={(event) => {
                        const imageElement =
                          event.target instanceof HTMLImageElement ? event.target : null;
                        openMenu(event, imageElement, fallbackUrl);
                      }}
                    />
                  ) : null}
                  {showDisplay && imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={showFallback ? "" : filename}
                      crossOrigin="use-credentials"
                      draggable={false}
                      className={
                        displayLoaded || !showFallback
                          ? IMAGE_CLASS
                          : "pointer-events-none absolute h-px w-px opacity-0"
                      }
                      onLoad={() => setDisplayLoaded(true)}
                      onError={() => setDisplayFailed(true)}
                      onContextMenu={(event) => {
                        if (!displayLoaded) return;
                        const imageElement =
                          event.target instanceof HTMLImageElement ? event.target : null;
                        openMenu(event, imageElement, imageUrl);
                      }}
                    />
                  ) : null}
                  {!showFallback && !displayLoaded && !displayFailed ? (
                    <Spinner className="h-8 w-8 text-white" />
                  ) : null}
                  {displayFailed && !showFallback ? (
                    <p className="px-6 text-center text-sm text-white">
                      Bild konnte nicht angezeigt werden.
                    </p>
                  ) : null}
                  <button
                    type="button"
                    className="absolute right-2 top-2 z-10 inline-flex h-11 w-11 items-center justify-center rounded-full bg-card text-label shadow-ios"
                    onClick={onClose}
                    aria-label="Schließen"
                  >
                    ✕
                  </button>
                  {canPrev ? (
                    <button
                      type="button"
                      className="absolute left-2 top-1/2 z-10 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-card/95 text-label shadow-ios"
                      onClick={goPrev}
                      aria-label="Vorheriges Foto"
                    >
                      <ChevronLeftIcon className="h-6 w-6" />
                    </button>
                  ) : null}
                  {canNext ? (
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 z-10 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-card/95 text-label shadow-ios"
                      onClick={goNext}
                      aria-label="Nächstes Foto"
                    >
                      <ChevronRightIcon className="h-6 w-6" />
                    </button>
                  ) : null}
                </div>

                {count > 1 ? (
                  <p className="mt-3 max-w-[calc(100vw-2rem)] truncate text-center text-sm text-white/90">
                    {filename}
                    <span className="text-white/60">
                      {" "}
                      · {safeIndex + 1} / {count}
                    </span>
                  </p>
                ) : null}

              </DialogPanel>
            </TransitionChild>
          </div>
        </Dialog>
      </Transition>
      {contextMenuNode}
    </>
  );
}
